import React from 'react';
import { X, Send, Mail, MessageSquare, AlertCircle, CheckCircle, Shuffle, Trophy } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import SMSCreditWarning from '../../../components/SMSCreditWarning';

const DrawingEntriesAdmin = () => {
  const [entries, setEntries] = React.useState([]);
  const [stats, setStats] = React.useState({ total: 0, new: 0, this_week: 0, customers: 0, vendors: 0, sms_subscribed: 0 });
  const [loading, setLoading] = React.useState(true);
  const [selectedEntries, setSelectedEntries] = React.useState(new Set());
  const [selectAllAcrossPages, setSelectAllAcrossPages] = React.useState(false); // Track if "Select All" means all pages
  const [filterStatus, setFilterStatus] = React.useState('all');
  const [filterType, setFilterType] = React.useState('all');
  
  // Winner Picker State
  const [eligibleCount, setEligibleCount] = React.useState(0);
  const [pickingWinner, setPickingWinner] = React.useState(false);
  const [winnerResult, setWinnerResult] = React.useState(null);
  
  // Blast Modal State
  const [showBlastModal, setShowBlastModal] = React.useState(false);
  const [blastChannel, setBlastChannel] = React.useState('email'); // email, sms, both
  const [blastSubject, setBlastSubject] = React.useState('');
  const [blastMessage, setBlastMessage] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [blastResult, setBlastResult] = React.useState(null);
  const [smsStatus, setSmsStatus] = React.useState(null);
  const [lastSmsMessage, setLastSmsMessage] = React.useState('');

  React.useEffect(() => {
    fetchEntries();
    fetchStats();
    fetchSmsStatus();
    fetchEligibleCount();
    // Load last SMS message from localStorage
    const savedLastSms = localStorage.getItem('lastSmsMessageDrawing');
    if (savedLastSms) {
      setLastSmsMessage(savedLastSms);
    }
  }, [filterStatus, filterType]);

  const fetchEntries = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/drawings?`;
      if (filterStatus !== 'all') url += `status=${filterStatus}&`;
      if (filterType !== 'all') url += `entry_type=${filterType}&`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEntries(data);
      }
    } catch (error) {
      console.error('Error fetching entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/drawings/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchSmsStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/drawings/blast/sms/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSmsStatus(data);
      }
    } catch (error) {
      console.error('Error fetching SMS status:', error);
    }
  };

  const fetchEligibleCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/drawings/eligible-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEligibleCount(data.eligible_count);
      }
    } catch (error) {
      console.error('Error fetching eligible count:', error);
    }
  };

  const pickRandomWinner = async () => {
    if (!window.confirm(`Pick a random winner from ${eligibleCount} eligible contacts?`)) return;
    
    setPickingWinner(true);
    setWinnerResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/drawings/pick-winner`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setWinnerResult({ success: true, ...data });
        fetchEntries();
        fetchStats();
        fetchEligibleCount();
      } else {
        setWinnerResult({ success: false, message: data.detail || 'Failed to pick winner' });
      }
    } catch (error) {
      setWinnerResult({ success: false, message: 'Error picking winner' });
    } finally {
      setPickingWinner(false);
    }
  };

  const pickManualWinner = async (contactId, contactName) => {
    if (!window.confirm(`Pick ${contactName} as this week's winner?`)) return;
    
    setPickingWinner(true);
    setWinnerResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/drawings/pick-winner-manual?contact_id=${contactId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setWinnerResult({ success: true, ...data });
        fetchEntries();
        fetchStats();
        fetchEligibleCount();
      } else {
        setWinnerResult({ success: false, message: data.detail || 'Failed to pick winner' });
      }
    } catch (error) {
      setWinnerResult({ success: false, message: 'Error picking winner' });
    } finally {
      setPickingWinner(false);
    }
  };

  const updateStatus = async (entryId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/drawings/${entryId}/status?status=${newStatus}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchEntries();
      fetchStats();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/drawings/${entryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchEntries();
      fetchStats();
    } catch (error) {
      console.error('Error deleting entry:', error);
    }
  };

  const toggleSelection = (entryId) => {
    setSelectAllAcrossPages(false); // Manual selection disables select all across pages
    setSelectedEntries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(entryId)) {
        newSet.delete(entryId);
      } else {
        newSet.add(entryId);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    if (selectedEntries.size === entries.length) {
      setSelectedEntries(new Set());
      setSelectAllAcrossPages(false);
    } else {
      setSelectedEntries(new Set(entries.map(e => e.id)));
      // If there are more contacts than currently displayed, set the flag
      if (stats.total > entries.length) {
        setSelectAllAcrossPages(true);
      } else {
        setSelectAllAcrossPages(false);
      }
    }
  };

  const selectByStatus = (status) => {
    const filtered = entries.filter(e => e.status === status);
    setSelectedEntries(new Set(filtered.map(e => e.id)));
    setSelectAllAcrossPages(false); // Manual selection disables select all across pages
  };

  const getSelectedEmails = () => {
    return entries.filter(e => selectedEntries.has(e.id)).map(e => e.email);
  };

  const getSelectedPhones = () => {
    return entries.filter(e => selectedEntries.has(e.id) && e.sms_consent).map(e => e.phone);
  };

  const getSelectedWithSmsConsent = () => {
    return entries.filter(e => selectedEntries.has(e.id) && e.sms_consent);
  };

  const exportSelected = (type) => {
    const selected = entries.filter(e => selectedEntries.has(e.id));
    let data;
    
    if (type === 'emails') {
      data = selected.map(e => e.email).join('\n');
    } else if (type === 'phones') {
      data = selected.filter(e => e.sms_consent).map(e => e.phone).join('\n');
    } else {
      data = selected.map(e => `${e.name},${e.email},${e.phone},${e.sms_consent ? 'Yes' : 'No'},${e.entry_type},${e.status}`).join('\n');
      data = 'Name,Email,Phone,SMS Consent,Type,Status\n' + data;
    }
    
    const blob = new Blob([data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `drawing_entries_${type}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Open blast modal
  const openBlastModal = () => {
    setBlastSubject('🍦 Special News from 1st Ave ICP!');
    setBlastMessage('Hi {{name}},\n\nWe have exciting news to share with you!\n\n[Your message here]\n\nThank you for being a valued customer!\n\n- The 1st Ave ICP Team');
    setBlastResult(null);
    setShowBlastModal(true);
  };

  // Send blast
  const sendBlast = async () => {
    if (selectedEntries.size === 0 && !selectAllAcrossPages) {
      alert('Please select at least one recipient');
      return;
    }

    if (blastChannel !== 'sms' && !blastSubject.trim()) {
      alert('Please enter an email subject');
      return;
    }

    if (!blastMessage.trim()) {
      alert('Please enter a message');
      return;
    }

    setSending(true);
    setBlastResult(null);

    const token = localStorage.getItem('token');
    const entryIds = selectAllAcrossPages ? [] : Array.from(selectedEntries);
    let results = { email: null, sms: null };

    try {
      // Send email blast
      if (blastChannel === 'email' || blastChannel === 'both') {
        const emailResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/drawings/blast/email`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            entry_ids: entryIds,
            subject: blastSubject,
            message: blastMessage,
            send_to_all: selectAllAcrossPages
          })
        });
        results.email = await emailResponse.json();
      }

      // Send SMS blast
      if (blastChannel === 'sms' || blastChannel === 'both') {
        const smsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/drawings/blast/sms`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            entry_ids: entryIds,
            message: blastMessage, // Telnyx handles long SMS via concatenation
            send_to_all: selectAllAcrossPages
          })
        });
        results.sms = await smsResponse.json();
      }

      setBlastResult(results);
      
      // Save last sent message for future reference (for SMS)
      if ((blastChannel === 'sms' || blastChannel === 'both') && (results.sms?.status === 'sent' || results.sms?.success)) {
        localStorage.setItem('lastSmsMessageDrawing', blastMessage);
        setLastSmsMessage(blastMessage);
      }
      
      // Refresh entries if emails were sent
      if (results.email?.success) {
        fetchEntries();
      }

    } catch (error) {
      console.error('Error sending blast:', error);
      setBlastResult({ error: error.message });
    } finally {
      setSending(false);
    }
  };

  const statusColors = {
    new: 'bg-green-100 text-green-800',
    contacted: 'bg-blue-100 text-blue-800',
    winner: 'bg-yellow-100 text-yellow-800 border-2 border-yellow-400',
    archived: 'bg-gray-100 text-gray-600'
  };

  if (loading) return <div>Loading drawing entries...</div>;

  return (
    <div>
      {/* SMS Credit Warning */}
      <SMSCreditWarning />
      
      {/* Stats Cards */}
      {/* Random Winner Picker */}
      <div className="bg-gradient-to-r from-pink-500 to-purple-500 rounded-xl shadow-lg p-6 mb-6 text-white">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-white/20 p-3 rounded-full">
              <Trophy className="w-8 h-8" />
            </div>
            <div>
              <h3 className="text-xl font-bold">Random Winner Picker</h3>
              <p className="text-white/80 text-sm">
                {eligibleCount} eligible contacts (SMS subscribed, haven't won)
              </p>
            </div>
          </div>
          <Button
            onClick={pickRandomWinner}
            disabled={pickingWinner || eligibleCount === 0}
            className="bg-white text-pink-600 hover:bg-gray-100 font-bold gap-2"
          >
            <Shuffle className={`w-5 h-5 ${pickingWinner ? 'animate-spin' : ''}`} />
            {pickingWinner ? 'Picking...' : 'Pick Random Winner'}
          </Button>
        </div>
        
        {/* Winner Result */}
        {winnerResult && (
          <div className={`mt-4 p-4 rounded-lg ${winnerResult.success ? 'bg-white/20' : 'bg-red-500/30'}`}>
            {winnerResult.success ? (
              <div>
                <p className="font-bold text-lg mb-2">🎉 {winnerResult.message}</p>
                <div className="grid md:grid-cols-2 gap-2 text-sm">
                  <p><strong>Name:</strong> {winnerResult.winner?.name}</p>
                  <p><strong>Email:</strong> {winnerResult.winner?.email}</p>
                  <p><strong>Source:</strong> {winnerResult.winner?.source}</p>
                  <p><strong>Picked from:</strong> {winnerResult.eligible_count} contacts</p>
                </div>
                <p className="text-xs mt-3 text-white/70">{winnerResult.note}</p>
              </div>
            ) : (
              <p className="font-bold">{winnerResult.message}</p>
            )}
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow p-4 text-center">
          <p className="text-3xl font-black text-pink-600">{stats.total}</p>
          <p className="text-sm text-gray-600">Total Contacts</p>
        </div>
        <div className="bg-white rounded-xl shadow p-4 text-center">
          <p className="text-3xl font-black text-green-600">{stats.sms_subscribed || 0}</p>
          <p className="text-sm text-gray-600">SMS Subscribed</p>
        </div>
        <div className="bg-white rounded-xl shadow p-4 text-center">
          <p className="text-3xl font-black text-blue-600">{stats.this_week}</p>
          <p className="text-sm text-gray-600">Entered This Week</p>
        </div>
        <div className="bg-white rounded-xl shadow p-4 text-center">
          <p className="text-3xl font-black text-purple-600">{stats.customers}</p>
          <p className="text-sm text-gray-600">Customers</p>
        </div>
        <div className="bg-white rounded-xl shadow p-4 text-center">
          <p className="text-3xl font-black text-orange-600">{stats.vendors}</p>
          <p className="text-sm text-gray-600">Vendors</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold">📋 All Contacts</h2>
            <p className="text-sm text-gray-500">Unified database - all contacts eligible for weekly drawing</p>
          </div>
          
          {/* Filters */}
          <div className="flex gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="all">All Statuses</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="winner">Winner</option>
              <option value="archived">Archived</option>
            </select>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="all">All Types</option>
              <option value="customer">Customers</option>
              <option value="vendor">Vendors</option>
            </select>
          </div>
        </div>

        {/* Selection Tools */}
        <div className="bg-pink-50 p-4 rounded-lg mb-4">
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedEntries.size === entries.length && entries.length > 0}
                onChange={selectAll}
                className="w-5 h-5 accent-pink-500"
              />
              <span className="font-medium">
                Select All ({selectAllAcrossPages ? `All ${stats.sms_subscribed || stats.total}` : selectedEntries.size} selected)
              </span>
            </label>
            <span className="text-gray-400">|</span>
            <button onClick={() => selectByStatus('new')} className="text-sm text-green-600 hover:underline">Select New</button>
            <button onClick={() => selectByStatus('contacted')} className="text-sm text-blue-600 hover:underline">Select Contacted</button>
            <button onClick={() => { setSelectedEntries(new Set()); setSelectAllAcrossPages(false); }} className="text-sm text-gray-600 hover:underline">Clear Selection</button>
          </div>
          
          {(selectedEntries.size > 0 || selectAllAcrossPages) && (
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="text-sm font-medium text-gray-700">
                Actions for {selectAllAcrossPages ? `All ${stats.sms_subscribed || stats.total}` : selectedEntries.size} selected:
              </span>
              <button
                onClick={openBlastModal}
                className="px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg text-sm hover:from-pink-600 hover:to-purple-600 flex items-center gap-2 font-semibold"
              >
                <Send className="w-4 h-4" />
                Compose & Send
              </button>
              <button
                onClick={() => exportSelected('emails')}
                className="px-3 py-1 bg-gray-500 text-white rounded-lg text-sm hover:bg-gray-600"
              >
                Export Emails
              </button>
              <button
                onClick={() => exportSelected('phones')}
                className="px-3 py-1 bg-gray-500 text-white rounded-lg text-sm hover:bg-gray-600"
              >
                Export Phones
              </button>
              <button
                onClick={() => exportSelected('full')}
                className="px-3 py-1 bg-gray-500 text-white rounded-lg text-sm hover:bg-gray-600"
              >
                Export Full CSV
              </button>
            </div>
          )}
        </div>

        {/* Entries List */}
        {entries.length === 0 ? (
          <p className="text-gray-600 text-center py-8">No contacts yet. Add contacts via CSV upload, manual entry, or the drawing entry form.</p>
        ) : (
          <div className="space-y-3">
            {entries.map((entry) => (
              <div 
                key={entry.id} 
                className={`border rounded-lg p-4 transition-colors ${
                  selectedEntries.has(entry.id) ? 'bg-pink-50 border-pink-300' : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={selectedEntries.has(entry.id)}
                    onChange={() => toggleSelection(entry.id)}
                    className="w-5 h-5 mt-1 accent-pink-500"
                  />
                  
                  {/* Main Info */}
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h4 className="font-bold text-lg">{entry.name}</h4>
                      <span className={`px-2 py-0.5 rounded text-xs ${statusColors[entry.status]}`}>
                        {entry.status === 'winner' ? '🏆 ' : ''}{entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        entry.entry_type === 'vendor' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {entry.entry_type}
                      </span>
                      {entry.sms_consent && (
                        <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">
                          📱 SMS OK
                        </span>
                      )}
                      {entry.source && (
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          entry.source === 'csv_upload' ? 'bg-cyan-100 text-cyan-800' :
                          entry.source === 'drawing_entry' ? 'bg-pink-100 text-pink-800' :
                          entry.source === 'contact_form' ? 'bg-amber-100 text-amber-800' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {entry.source === 'csv_upload' ? '📁 CSV' :
                           entry.source === 'drawing_entry' ? '🎰 Drawing' :
                           entry.source === 'contact_form' ? '📝 Form' :
                           entry.source === 'manual' ? '✏️ Manual' : entry.source}
                        </span>
                      )}
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-2 text-sm">
                      <p><span className="text-gray-500">Email:</span> {entry.email || '-'}</p>
                      <p><span className="text-gray-500">Phone:</span> {entry.phone || '-'}</p>
                    </div>
                    
                    {entry.entry_type === 'vendor' && (
                      <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                        <p><strong>Company:</strong> {entry.company_name} | <strong>Phone:</strong> {entry.company_phone}</p>
                        <p><strong>Website:</strong> {entry.website_url} | <strong>Position:</strong> {entry.position}</p>
                        <p><strong>Best Contact:</strong> {entry.best_contact_method}</p>
                      </div>
                    )}
                    
                    {entry.comment && (
                      <p className="mt-2 text-sm text-gray-600 italic">&ldquo;{entry.comment}&rdquo;</p>
                    )}
                    
                    <p className="text-xs text-gray-400 mt-2">
                      Added: {new Date(entry.created_at).toLocaleString()}{entry.last_entry_date ? ` | Last Entry: ${new Date(entry.last_entry_date).toLocaleDateString()}` : ''}{entry.ip_address ? ` | IP: ${entry.ip_address}` : ''}
                    </p>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    <select
                      value={entry.status}
                      onChange={(e) => updateStatus(entry.id, e.target.value)}
                      className="text-sm px-2 py-1 border rounded"
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="winner">🏆 Winner</option>
                      <option value="archived">Archived</option>
                    </select>
                    {entry.status !== 'winner' && (
                      <button
                        onClick={() => pickManualWinner(entry.contact_id || entry.id, entry.name)}
                        className="text-yellow-600 hover:text-yellow-700 text-sm font-medium flex items-center gap-1 justify-center bg-yellow-50 px-2 py-1 rounded border border-yellow-200 hover:bg-yellow-100"
                        title="Pick as Winner"
                      >
                        <Trophy className="w-3 h-3" />
                        Pick Winner
                      </button>
                    )}
                    <button
                      onClick={() => deleteEntry(entry.id)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ============== BLAST MODAL ============== */}
      {showBlastModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-2xl font-bold flex items-center gap-2">
                  <Send className="w-6 h-6 text-pink-500" />
                  Compose & Send Message
                </h3>
                <p className="text-gray-500 text-sm mt-1">
                  {selectAllAcrossPages 
                    ? `All ${stats.sms_subscribed || stats.total} SMS subscribers selected (across all pages)`
                    : `${selectedEntries.size} recipient${selectedEntries.size !== 1 ? 's' : ''} selected`
                  }
                </p>
              </div>
              <button onClick={() => setShowBlastModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Channel Selection */}
            <div className="mb-6">
              <label className="block text-sm font-bold mb-3">Choose Email, SMS, or Both</label>
              <div className="grid grid-cols-3 gap-3">
                <button
                  onClick={() => setBlastChannel('email')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    blastChannel === 'email' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Mail className={`w-8 h-8 mx-auto mb-2 ${blastChannel === 'email' ? 'text-blue-500' : 'text-gray-400'}`} />
                  <div className="font-bold">Email Only</div>
                  <div className="text-xs text-gray-500">
                    {selectAllAcrossPages ? `All ${stats.total}` : getSelectedEmails().length} recipients
                  </div>
                </button>
                
                <button
                  onClick={() => setBlastChannel('sms')}
                  className={`p-4 rounded-xl border-2 transition-all relative ${
                    blastChannel === 'sms' 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <MessageSquare className={`w-8 h-8 mx-auto mb-2 ${blastChannel === 'sms' ? 'text-green-500' : 'text-gray-400'}`} />
                  <div className="font-bold">SMS Only</div>
                  <div className="text-xs text-gray-500">
                    {selectAllAcrossPages ? `All ${stats.sms_subscribed || 0}` : getSelectedWithSmsConsent().length} with consent
                  </div>
                  {smsStatus?.status === 'ready' && (
                    <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                      Ready
                    </div>
                  )}
                  {smsStatus?.status !== 'ready' && (
                    <div className="absolute -top-2 -right-2 bg-yellow-400 text-yellow-900 text-xs px-2 py-0.5 rounded-full font-bold">
                      Setup
                    </div>
                  )}
                </button>
                
                <button
                  onClick={() => setBlastChannel('both')}
                  className={`p-4 rounded-xl border-2 transition-all relative ${
                    blastChannel === 'both' 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex justify-center gap-1 mb-2">
                    <Mail className={`w-6 h-6 ${blastChannel === 'both' ? 'text-purple-500' : 'text-gray-400'}`} />
                    <MessageSquare className={`w-6 h-6 ${blastChannel === 'both' ? 'text-purple-500' : 'text-gray-400'}`} />
                  </div>
                  <div className="font-bold">Both</div>
                  <div className="text-xs text-gray-500">Email + SMS</div>
                </button>
              </div>
            </div>

            {/* SMS Status Info */}
            {(blastChannel === 'sms' || blastChannel === 'both') && smsStatus && (
              <div className={`mb-6 p-4 rounded-xl border-2 ${
                smsStatus.status === 'ready' 
                  ? 'bg-green-50 border-green-300' 
                  : 'bg-yellow-50 border-yellow-300'
              }`}>
                <div className="flex items-start gap-3">
                  {smsStatus.status === 'ready' ? (
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div>
                    {smsStatus.status === 'ready' ? (
                      <>
                        <h4 className="font-bold text-green-800">✅ SMS Ready to Send</h4>
                        <p className="text-sm text-green-700 mt-1">
                          Telnyx SMS is configured and ready. Messages will be sent from {smsStatus.phone_number}.
                        </p>
                      </>
                    ) : (
                      <>
                        <h4 className="font-bold text-yellow-800">⏳ SMS Not Configured</h4>
                        <p className="text-sm text-yellow-700 mt-1">
                          {smsStatus.message || 'SMS messaging is not configured. Please add Telnyx credentials.'}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Last SMS Message - Quick Copy */}
            {(blastChannel === 'sms' || blastChannel === 'both') && lastSmsMessage && (
              <div className="mb-6 bg-purple-50 border-2 border-purple-200 rounded-xl p-4">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-bold text-purple-800">📋 Last Sent SMS Message</h4>
                  <button
                    onClick={() => setBlastMessage(lastSmsMessage)}
                    className="px-3 py-1 bg-purple-500 text-white text-sm rounded-lg hover:bg-purple-600 font-medium"
                  >
                    Use This Message
                  </button>
                </div>
                <div className="bg-white p-3 rounded-lg border text-sm text-gray-600 whitespace-pre-wrap max-h-24 overflow-y-auto">
                  {lastSmsMessage.length > 150 ? lastSmsMessage.substring(0, 150) + '...' : lastSmsMessage}
                </div>
              </div>
            )}

            {/* Subject Line (Email only) */}
            {(blastChannel === 'email' || blastChannel === 'both') && (
              <div className="mb-4">
                <label className="block text-sm font-bold mb-2">📧 Email Subject Line</label>
                <input
                  type="text"
                  value={blastSubject}
                  onChange={(e) => setBlastSubject(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
                  placeholder="Enter email subject..."
                />
              </div>
            )}

            {/* Message Content */}
            <div className="mb-4">
              <label className="block text-sm font-bold mb-2">
                ✏️ Message Content
                <span className="font-normal text-gray-500 ml-2">
                  Use {'{{name}}'} to personalize
                </span>
              </label>
              <textarea
                value={blastMessage}
                onChange={(e) => setBlastMessage(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border-2 focus:border-pink-500 focus:outline-none font-mono text-sm"
                rows="8"
                placeholder="Enter your message..."
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>{blastMessage.length} characters</span>
                {(blastChannel === 'sms' || blastChannel === 'both') && (
                  <span className={blastMessage.length > 160 ? 'text-orange-500' : ''}>
                    SMS: {Math.ceil(blastMessage.length / 160)} segment{Math.ceil(blastMessage.length / 160) !== 1 ? 's' : ''} 
                    (160 char/segment)
                  </span>
                )}
              </div>
            </div>

            {/* Preview */}
            <div className="mb-6 p-4 bg-gray-50 rounded-xl">
              <h4 className="font-bold mb-2">👀 Preview (for &quot;John Doe&quot;)</h4>
              <div className="bg-white p-3 rounded-lg border text-sm whitespace-pre-wrap">
                {blastMessage.replace('{{name}}', 'John Doe')}
              </div>
            </div>

            {/* Results */}
            {blastResult && (
              <div className={`mb-6 p-4 rounded-xl ${
                blastResult.error ? 'bg-red-50 border-2 border-red-200' : 'bg-green-50 border-2 border-green-200'
              }`}>
                {blastResult.error ? (
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertCircle className="w-5 h-5" />
                    <span>Error: {blastResult.error}</span>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {blastResult.email && (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="text-green-800">
                          <strong>Email:</strong> {blastResult.email.successful}/{blastResult.email.total} sent successfully
                          {blastResult.email.failed > 0 && ` (${blastResult.email.failed} failed)`}
                        </span>
                      </div>
                    )}
                    {blastResult.sms && (
                      <div className="flex items-center gap-2">
                        {blastResult.sms.status === 'not_configured' ? (
                          <>
                            <AlertCircle className="w-5 h-5 text-yellow-600" />
                            <span className="text-yellow-800">
                              <strong>SMS:</strong> Not configured - {blastResult.sms.message}
                            </span>
                          </>
                        ) : blastResult.sms.status === 'sent' ? (
                          <>
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <span className="text-green-800">
                              <strong>SMS:</strong> {blastResult.sms.successful}/{blastResult.sms.total} sent successfully
                              {blastResult.sms.failed > 0 && ` (${blastResult.sms.failed} failed)`}
                            </span>
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <span className="text-green-800">
                              <strong>SMS:</strong> {blastResult.sms.message}
                            </span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowBlastModal(false)}
                className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={sendBlast}
                disabled={sending}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg hover:from-pink-600 hover:to-purple-600 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {sending ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send Blast
                  </>
                )}
              </button>
            </div>

            {/* 10DLC Info Footer */}
            {(blastChannel === 'sms' || blastChannel === 'both') && (
              <div className="mt-4 p-3 bg-gray-100 rounded-lg text-xs text-gray-600">
                <strong>📋 10DLC Compliance:</strong> SMS marketing in the US requires 10DLC registration. 
                Your messages are being sent via Telnyx. All recipients have provided SMS consent and can 
                reply STOP to unsubscribe.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DrawingEntriesAdmin;
